import { useState } from "react";
import { switchLocale as localeHref } from "../i18n/language-switcher";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Check, Globe } from "lucide-react";
import { SUPPORTED_LOCALES, switchLanguage, type SupportedLocale } from "../../lib/i18n";
import { CURRENCY_CODES, currencySymbolFor, useCurrency, type CurrencyCode } from "../../lib/currency";
import { currencyLabel } from "../../lib/format";
import { useSavePreferences } from "../../features/account/hooks/use-preference-sync";
import { panelFrameStyle, panelInnerStyle } from "./card-frame";
import { FLOAT_FRAME_SHADOW, popOrigin, useMenu, usePresence, type MenuGroup } from "./use-presence";
import { BEVEL_CHIP_ON, BEVEL_NAVY_FACE } from "@majourneys/shared/ui";

const NAVY = "#110E39";
const GOLD_DARK = "#8A6425";
const MUTED = "#5a5d77";
const HAIRLINE = "rgba(17,14,57,0.09)";
const HAIRLINE_STRONG = "rgba(17,14,57,0.13)";
const SOFT_TINT = "rgba(17,14,57,0.055)";
const NAVY_ON = { background: BEVEL_NAVY_FACE, color: "#fff", boxShadow: BEVEL_CHIP_ON };
/** The frame is 22 outside and 18 inside; the band's corners follow the inner radius. */
const PANEL_RADIUS = 18;
const PANEL_INNER_RADIUS = 14;

type LocaleMeta = {
  native: string;
  flag: string;
};

const LOCALE_META: Record<SupportedLocale, LocaleMeta> = {
  en: { native: "English", flag: "🇬🇧" },
  fr: { native: "Français", flag: "🇫🇷" },
  ar: { native: "العربية", flag: "🇲🇦" },
  es: { native: "Español", flag: "🇪🇸" },
  it: { native: "Italiano", flag: "🇮🇹" },
  de: { native: "Deutsch", flag: "🇩🇪" },
  nl: { native: "Nederlands", flag: "🇳🇱" },
};

type CurrencyMeta = {
  code: CurrencyCode;
  symbol: string;
};

/**
 * Derived from the shared list, not hand-written.
 *
 * This used to be a literal of three codes, so the mobile header offered
 * MAD/USD/EUR while the desktop switcher offered four — a guest who picked GBP
 * on a laptop opened the same site on a phone and found their currency was not
 * on the menu.
 */
const CURRENCIES: readonly CurrencyMeta[] = CURRENCY_CODES.map((code) => ({
  code,
  symbol: currencySymbolFor(code),
}));

type TabKey = "language" | "currency";

/** The labels fold on the header's clock, so the whole bar moves together. */
const LABEL_FOLD =
  "transition-[max-width,opacity,gap,padding] duration-200 ease-[var(--mj-ease-out)] motion-reduce:transition-none";

/**
 * The phone's one right-side control, and the dialog that hangs from it.
 *
 * `compact` folds the two labels away and leaves the globe, for the scrolled
 * header where the row belongs to the search. The button stays a button — the
 * labels collapse, the control never unmounts.
 */
export function LocaleDropdown({
  group,
  compact = false,
  align = "end",
}: {
  group?: MenuGroup;
  compact?: boolean;
  /** Which edge of the globe the panel hangs from. "start" when the globe sits at the bar's start. */
  align?: "start" | "end";
}) {
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const currentLang = (lang ?? "en") as SupportedLocale;
  const navigate = useNavigate();
  const { t, i18n: i18nInstance } = useTranslation();
  const menu = useMenu(group, "mobile-locale");
  const presence = usePresence(menu.open);

  /**
   * The shared context, not local state.
   *
   * This component held `useState("MAD")` and wrote the guest's pick into it
   * and nowhere else, so choosing a currency on mobile relabelled this button
   * and changed no price on the page. It was invisible because the desktop
   * switcher — a different component — was wired correctly.
   */
  const { currency, setCurrency } = useCurrency();
  const { save } = useSavePreferences();
  const [tab, setTab] = useState<TabKey>("language");

  function switchLocale(newLang: SupportedLocale) {
    // Loads the strings before switching; the layout's beforeLoad shares the
    // same in-flight request, so the navigation below cannot beat it.
    void switchLanguage(newLang);
    void save({ language: newLang });
    // The whole href, not the pathname: the query holds the guest's dates,
    // party, filters and booking choices.
    const { pathname, search, hash } = window.location;
    navigate({ href: localeHref(`${pathname}${search}${hash}`, newLang), resetScroll: false });
    menu.close();
  }

  function pickCurrency(code: CurrencyCode) {
    setCurrency(code);
    void save({ currency: code });
    menu.close();
  }

  const currentCurrency = CURRENCIES.find((c) => c.code === currency) ?? CURRENCIES[0];
  // Only the label localizes: "MAD" reads "درهم" on Arabic pages.
  const currencyText = currencyLabel(currentCurrency.code, currentLang);

  return (
    <div ref={menu.ref} className="relative">
      <button
        type="button"
        onClick={() => {
          if (!menu.open) setTab("language");
          menu.toggle();
        }}
        aria-haspopup="dialog"
        aria-expanded={menu.open}
        title={t("common.locale_title", { language: t(`common.language_${currentLang}`), currency: currencyText })}
        className={`mj-press inline-flex h-10 items-center rounded-full text-[12px] font-bold uppercase ${LABEL_FOLD} ${
          compact ? "gap-0 px-2.5" : "gap-2 px-3"
        }`}
        style={{ letterSpacing: "0.06em", ...(menu.open ? NAVY_ON : { background: SOFT_TINT, color: NAVY }) }}
      >
        <Globe size={17} strokeWidth={2.2} className="shrink-0" aria-hidden />
        <span
          className={`flex items-center overflow-hidden ${LABEL_FOLD} ${
            compact ? "max-w-0 gap-0 opacity-0" : "max-w-[80px] gap-2 opacity-100"
          }`}
          aria-hidden={compact || undefined}
        >
          <span className="leading-none">{currentLang}</span>
          <span className="h-3 w-px" style={{ background: menu.open ? "rgba(255,255,255,0.35)" : HAIRLINE_STRONG }} aria-hidden="true" />
          <span className="leading-none">{currencyText}</span>
        </span>
      </button>

      {presence.mounted ? (
        <div
          ref={presence.ref}
          role="dialog"
          aria-label={t("common.regional_preferences")}
          data-state={presence.state}
          data-instant={menu.instant ? "true" : undefined}
          className={`mj-pop absolute ${align === "start" ? "start-0" : "end-0"} top-[calc(100%+8px)] z-[var(--z-dropdown)] w-[320px] max-w-[calc(100vw-24px)] ${
            presence.state === "closed" ? "pointer-events-none" : ""
          }`}
          style={{ ...panelFrameStyle(PANEL_RADIUS), boxShadow: FLOAT_FRAME_SHADOW, transformOrigin: popOrigin(align, i18nInstance.dir()) }}
        >
          {/* Two elements: the list scrolls, so the inner card has to clip. */}
          <div className="overflow-hidden" style={panelInnerStyle(PANEL_RADIUS)}>
            <div
              className="flex gap-1 p-1.5"
              style={{ background: SOFT_TINT, boxShadow: `inset 0 -1px 0 ${HAIRLINE}`, borderRadius: `${PANEL_INNER_RADIUS}px ${PANEL_INNER_RADIUS}px 0 0` }}
            >
              <TabButton active={tab === "language"} onClick={() => setTab("language")}>
                {t("common.language")}
              </TabButton>
              <TabButton active={tab === "currency"} onClick={() => setTab("currency")}>
                {t("common.currency")}
              </TabButton>
            </div>

            <div className="max-h-[360px] overflow-y-auto p-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {tab === "language" ? (
                <ul role="listbox" aria-label={t("common.language")} className="mj-stagger flex flex-col gap-0.5">
                  {SUPPORTED_LOCALES.map((l) => {
                    const meta = LOCALE_META[l];
                    const isActive = l === currentLang;
                    return (
                      <li key={l}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          onClick={() => switchLocale(l)}
                          className="flex w-full items-center gap-3 rounded-[12px] px-2.5 py-2 text-start hover:bg-black/5"
                          style={{ background: isActive ? SOFT_TINT : undefined }}
                        >
                          <span
                            aria-hidden="true"
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[18px] leading-none"
                            style={{ background: SOFT_TINT }}
                          >
                            {meta.flag}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[13.5px] font-bold" style={{ color: NAVY }}>
                              {meta.native}
                            </span>
                            <span className="block text-[11px]" style={{ color: MUTED }}>
                              {t(`common.language_${l}`)}
                            </span>
                          </span>
                          {isActive ? <Check size={15} strokeWidth={2.5} className="shrink-0" style={{ color: GOLD_DARK }} aria-hidden /> : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <ul role="listbox" aria-label={t("common.currency")} className="mj-stagger flex flex-col gap-0.5">
                  {CURRENCIES.map((c) => {
                    const isActive = c.code === currency;
                    return (
                      <li key={c.code}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          onClick={() => pickCurrency(c.code)}
                          className="flex w-full items-center gap-3 rounded-[12px] px-2.5 py-2 text-start hover:bg-black/5"
                          style={{ background: isActive ? SOFT_TINT : undefined }}
                        >
                          <span
                            aria-hidden="true"
                            className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-[12px] font-bold"
                            style={{ background: SOFT_TINT, color: NAVY }}
                          >
                            {c.symbol}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[13.5px] font-bold" style={{ color: NAVY }}>
                              {c.code}
                            </span>
                            <span className="block text-[11px]" style={{ color: MUTED }}>
                              {t(`common.currency_${c.code}`)}
                            </span>
                          </span>
                          {isActive ? <Check size={15} strokeWidth={2.5} className="shrink-0" style={{ color: GOLD_DARK }} aria-hidden /> : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="mj-press min-h-7 flex-1 rounded-full py-1.5 text-[12px] font-bold"
      style={active ? NAVY_ON : { color: MUTED }}
    >
      {children}
    </button>
  );
}
